import React from 'react';
import { AuditResult, FlagAuditResult, SpeedAuditVersion } from '@scale/shared/defaults';
import LookupObjectLink from 'react-scale/corp/components/Lookup/LookupObjectLink';
import IconToggle from 'design-system/IconToggle';
import classnames from 'classnames';
import { Button } from 'design-system/Form';
import {
  AuditLookup,
  AuditLookupType,
  SpeedAuditLookupResponse,
} from '@scale/shared/types/corp/attemptAudit';
import { PerAnnotationError } from '@scale/shared/types/customerAudit';
import { NormalizedAssignment } from '@scale/shared/assignmentJson/legacy/types/NormalizedAssignment';
// eslint-disable-next-line restrict-imports/restrict-imports
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { logCorpEvent } from '@scale/frontend-lib/analytics/log-corp-event';
import { Event } from '@scale/shared/types/events';
import { speedAuditEventPrefix } from 'react-scale/components/audits/SpeedAudit/utils';
import {
  ElementClickedType,
  GeneralElementClicked,
} from '@scale/shared/types/events/GeneralElementClicked';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

function SpeedAuditHeaderV1(props: {
  styles: ClassNameMap<any>;
  name: string;
  id: string;
  openKeyboardShortcuts: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  headerId: string | undefined;
  flagAudit: undefined | boolean;
  subtaskAssignment: {
    auditLookup: AuditLookup | undefined;
    subtask: NormalizedAssignment | undefined;
    responses: SpeedAuditLookupResponse[] | undefined;
    attemptAuditId?: string;
    result?: AuditResult;
    flagResult?: FlagAuditResult;
    comments?: string;
    taskInstructionItems?: string[];
    perAnnotationInstructionItems?: PerAnnotationError[];
  };
  auditLookup: { id?: string; type?: AuditLookupType };
  index: number;
  onPreviousUnauditedObject: () => void;
  onPreviousObject: () => void;
  numObjects: number;
  numSubtaskAssignments: number;
  completed: boolean;
  missingRequiredGrade: boolean;
  onNextObject: () => void;
  onNextUnauditedObject: () => void;
  onRestart: () => void;
  projectId: string;
  metadataDescription: undefined | JSX.Element;
  onRequestClose: () => void;
}) {
  const { isRemoAdmin } = useRemoAdminCtx();
  return (
    <div className={props.styles.headerContainer}>
      <div className={props.styles.header}>
        <div className={props.styles.block}>
          <div>
            <h1 className={props.styles.title}>Speed Audit</h1>
            <div>
              Batch: {props.name}
              <br />(<code>{props.id}</code>)
            </div>
          </div>
          <a
            className={props.styles.helpLink}
            href="#"
            role="button"
            onClick={props.openKeyboardShortcuts}
          >
            <HelpOutlineIcon width="16" height="16" fill="currentColor" />
            <span className={props.styles.helpLinkText}>Keyboard Shortcuts</span>
          </a>
        </div>
        <div className={props.styles.nav}>
          {props.headerId && (
            <div>
              <span style={{ textTransform: 'capitalize' }}>
                {(props.flagAudit ? 'subtask' : props.subtaskAssignment?.auditLookup?.type) ||
                  props.auditLookup.type}
              </span>{' '}
              <LookupObjectLink
                shouldOpenNewTab
                onClick={() =>
                  logCorpEvent(Event.GeneralElementClicked, {
                    name: `${speedAuditEventPrefix(
                      SpeedAuditVersion.v1,
                    )} > Current Subtask Id Clicked `,
                    type: ElementClickedType.Link,
                  } as GeneralElementClicked)
                }
                objectId={props.headerId}
              />
            </div>
          )}
          <div className={props.styles.navArrows}>
            <IconToggle
              name="previous-unaudited-object"
              icon="arrow-left"
              isOn
              isDisabled={props.index === 0}
              onClick={props.onPreviousUnauditedObject}
              tooltip="Skip to Previous Unaudited Object"
            />
            <IconToggle
              name="previous-object"
              icon="chevron-left"
              isOn
              isDisabled={props.index === 0}
              onClick={props.onPreviousObject}
              tooltip="Previous Object"
            />
            <div>
              {props.index + 1} / {props.numObjects} Audits
              <br />(
              {props.numObjects === props.numSubtaskAssignments
                ? 'Done loading'
                : `Still loading ${props.numObjects - props.numSubtaskAssignments} Audits`}
              )
            </div>
            <IconToggle
              name="next-object"
              icon="chevron-right"
              isOn
              isDisabled={props.completed || props.missingRequiredGrade}
              onClick={props.onNextObject}
              tooltip={
                props.missingRequiredGrade
                  ? 'You must select a grade to continue to Next Object'
                  : 'Next Object'
              }
            />
            <IconToggle
              name="next-unaudited-object"
              icon="arrow-right"
              isOn
              isDisabled={props.completed || props.missingRequiredGrade}
              onClick={props.onNextUnauditedObject}
              tooltip={
                props.missingRequiredGrade
                  ? 'You must select a grade to continue to Skip to Next Unaudited Object'
                  : 'Skip to Next Unaudited Object'
              }
            />
          </div>
        </div>
        <div
          className={classnames(props.styles.blockRight, {
            [props.styles.blockComplete]: props.completed,
          })}
        >
          {props.completed && (
            <div className={props.styles.blockCompleteContent}>
              <h3>Speed Audit Complete</h3>
              <p>Scroll down to see summary and download CSV</p>
              <Button type="button" onClick={props.onRestart}>
                Restart Speed Audit
              </Button>
              <br />
            </div>
          )}
          <div className={props.styles.blockRightButtons}>
            {!isRemoAdmin && (
              <a
                href={`/corp/quality_lab?project=${props.projectId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  logCorpEvent(Event.GeneralElementClicked, {
                    name: `${speedAuditEventPrefix(SpeedAuditVersion.v1)} > Go To Quality Lab`,
                    type: ElementClickedType.Link,
                  } as GeneralElementClicked)
                }
              >
                <Button yellow>Go to Quality Lab</Button>
              </a>
            )}
            <Button
              yellow
              onClick={() => {
                props.onRequestClose();
                logCorpEvent(Event.GeneralElementClicked, {
                  name: `${speedAuditEventPrefix(SpeedAuditVersion.v1)} > Close Batch`,
                  type: ElementClickedType.Button,
                } as GeneralElementClicked);
              }}
            >
              Close Batch
            </Button>
          </div>
        </div>
      </div>
      {props.metadataDescription && (
        <div className={props.styles.nav} style={{ maxWidth: '90vw' }}>
          Tasker Scores:
          <code style={{ fontSize: 12, marginLeft: 12 }}>
            <strong>{props.metadataDescription}</strong>
          </code>
        </div>
      )}
    </div>
  );
}

export default SpeedAuditHeaderV1;

function HelpOutlineIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" {...props}>
      <path fill="none" d="M0 0h24v24H0z" />
      <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
    </svg>
  );
}
